export type BitrixTracePage = {
  url: string;
  path: string;
  title: string | null;
  at: string | null;
};

export type ParsedBitrixTrace = {
  pages: BitrixTracePage[];
  previousPages: BitrixTracePage[];
  currentUrl: string | null;
  yaId: string | null;
  gclid: string | null;
  isMobile: boolean | null;
  fingerprint: string;
};

const INTERNAL_PATH_RE = /^\/(admin|api)(\/|$)/i;

export function isInternalJourneyPath(urlOrPath: string): boolean {
  try {
    const path = urlOrPath.startsWith("/")
      ? urlOrPath
      : new URL(urlOrPath).pathname;
    return INTERNAL_PATH_RE.test(path);
  } catch {
    return /\/admin(?:\/|$)/i.test(urlOrPath);
  }
}

function pathFromUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}` || parsed.pathname;
  } catch {
    return url;
  }
}

function parsePageRow(row: unknown): BitrixTracePage | null {
  if (!Array.isArray(row) || typeof row[0] !== "string") return null;
  const url = row[0].trim();
  if (!url || isInternalJourneyPath(url)) return null;
  const ts = typeof row[1] === "number" ? row[1] : null;
  const title = typeof row[2] === "string" ? row[2].trim() : null;
  return {
    url,
    path: pathFromUrl(url),
    title: title || null,
    at: ts ? new Date(ts * 1000).toISOString() : null,
  };
}

function parsePageList(list: unknown): BitrixTracePage[] {
  if (!Array.isArray(list)) return [];
  const pages: BitrixTracePage[] = [];
  const seen = new Set<string>();
  for (const row of list) {
    const page = parsePageRow(row);
    if (!page) continue;
    const key = page.path.replace(/\/$/, "") || "/";
    if (seen.has(key)) continue;
    seen.add(key);
    pages.push(page);
  }
  return pages;
}

export function parseBitrixTrace(
  raw: string | null | undefined,
): ParsedBitrixTrace | null {
  if (!raw?.trim()) return null;
  try {
    const parsed = JSON.parse(raw) as {
      url?: unknown;
      pages?: { list?: unknown };
      previous?: { list?: unknown };
      client?: { yaId?: unknown };
      device?: { isMobile?: unknown };
      tags?: { gclid?: unknown };
    };
    const pages = parsePageList(parsed.pages?.list);
    const previousPages = parsePageList(parsed.previous?.list);
    if (pages.length === 0 && previousPages.length === 0) {
      if (typeof parsed.url === "string" && !isInternalJourneyPath(parsed.url)) {
        const url = parsed.url.trim();
        pages.push({
          url,
          path: pathFromUrl(url),
          title: null,
          at: null,
        });
      }
    }
    if (pages.length === 0 && previousPages.length === 0) return null;

    const yaId =
      typeof parsed.client?.yaId === "string" && parsed.client.yaId.trim()
        ? parsed.client.yaId.trim()
        : null;
    const gclid =
      typeof parsed.tags?.gclid === "string" && parsed.tags.gclid.trim()
        ? parsed.tags.gclid.trim()
        : null;
    const isMobile =
      typeof parsed.device?.isMobile === "boolean"
        ? parsed.device.isMobile
        : null;
    const currentUrl =
      typeof parsed.url === "string" && parsed.url.trim()
        ? parsed.url.trim()
        : pages.at(-1)?.url ?? null;

    const fingerprint = [
      ...pages.map((p) => p.path),
      previousPages.length ? `prev:${previousPages.length}` : "",
      yaId ? `ya:${yaId}` : "",
    ]
      .filter(Boolean)
      .join("|");

    return {
      pages,
      previousPages,
      currentUrl,
      yaId,
      gclid,
      isMobile,
      fingerprint,
    };
  } catch {
    return null;
  }
}

/** Совместимость: только клиентские страницы из трейса B24. */
export function parseBitrixTracePages(
  raw: string | null | undefined,
): BitrixTracePage[] {
  return parseBitrixTrace(raw)?.pages ?? [];
}
